import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, Observable, throwError, timer } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { DeploymentMonitorService } from './deployment-monitor.service';
import { EnvironmentService } from './environment/environment.service';

const RETRY_DELAY_MS = 20000;
const MAX_WAIT_MS = 120000;

/**
 * Holds requests for our own API hosts when the host goes unhealthy mid-session - the deploy /
 * app-recycle window that surfaces either as a CORS preflight failure (status 0, when the
 * gateway's error response lacks CORS headers) or as a real 5xx (when the gateway's error response
 * carries them). While the outage is confirmed server-side, each failed request is retried every
 * {@link RETRY_DELAY_MS} up to {@link MAX_WAIT_MS} instead of erroring back to the caller, so a
 * transient deploy doesn't white-screen the app or drop the user's in-flight work.
 *
 * Registered outermost so each retry re-runs the whole pipeline (auth attach/refresh included).
 */
@Injectable()
export class DeploymentMonitorInterceptor implements HttpInterceptor {

  private _apiUrlPrefixes: string[] | undefined;

  constructor(
    private readonly _monitor: DeploymentMonitorService,
    private readonly _environmentService: EnvironmentService) { }

  intercept = (request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> => {
    if (!this.isApiRequest(request.url)) {
      return next.handle(request);
    }
    const windowStart = Date.now();
    return next.handle(request).pipe(
      catchError(error => this.afterFailure(request, next, windowStart, error,
        lastError => this.holdThroughOutage(request, next, windowStart, lastError))),
    );
  };

  /**
   * Owns the request's single overlay slot for the rest of the wait. The monitor pair wraps the
   * whole retry loop once per request - not once per retry - so the waiting-request count tracks
   * held requests rather than retry depth.
   */
  private holdThroughOutage = (request: HttpRequest<any>, next: HttpHandler, windowStart: number,
    error: HttpErrorResponse): Observable<HttpEvent<any>> => {
    this._monitor.beginWait();
    return this.retryWhileDown(request, next, windowStart, error).pipe(
      finalize(() => this._monitor.endWait()),
    );
  };

  private retryWhileDown = (request: HttpRequest<any>, next: HttpHandler, windowStart: number,
    lastError: HttpErrorResponse): Observable<HttpEvent<any>> => {
    const remaining = MAX_WAIT_MS - (Date.now() - windowStart);
    if (remaining <= 0) {
      return throwError(() => lastError);
    }
    return timer(Math.min(RETRY_DELAY_MS, remaining)).pipe(
      switchMap(() => next.handle(request).pipe(
        catchError(error => this.afterFailure(request, next, windowStart, error,
          retryError => this.retryWhileDown(request, next, windowStart, retryError))),
      )),
    );
  };

  private afterFailure = (request: HttpRequest<any>, next: HttpHandler, windowStart: number,
    error: any, whenConfirmed: (lastError: HttpErrorResponse) => Observable<HttpEvent<any>>): Observable<HttpEvent<any>> => {
    const remaining = MAX_WAIT_MS - (Date.now() - windowStart);
    if (!this.isRetryableFailure(error) || remaining <= 0) {
      return throwError(() => error);
    }
    return from(this._monitor.confirmApiOutage(request.url)).pipe(
      switchMap(confirmed => (confirmed ? whenConfirmed(error) : throwError(() => error))),
    );
  };

  private isApiRequest = (url: string): boolean => {
    if (!this._apiUrlPrefixes) {
      const info = this._environmentService.apiInfo;
      this._apiUrlPrefixes = [
        info.apiBaseUrl,
        info.publicApiBaseUrl,
        info.docuViewareApiBaseUrl,
        info.jobApiBaseUrl,
        info.docApiBaseUrl,
        info.authApiBaseUrl,
      ].filter((u): u is string => !!u).map(u => u.toLowerCase());
    }
    const lower = url.toLowerCase();
    return this._apiUrlPrefixes.some(prefix => lower.startsWith(prefix));
  };

  /**
   * Two retry-eligible shapes: an XHR-level failure (status 0 with the network `ProgressEvent` - a
   * CORS-blocked or dropped connection), or a real 5xx (the gateway/health-layer response some
   * environments DO attach CORS headers to). The probe in {@link DeploymentMonitorService}
   * discriminates whole-service health, so a legit single-endpoint 5xx still propagates normally.
   * Aborted requests and synthetic status-0 errors built in code are never retried.
   */
  private isRetryableFailure = (error: any): boolean =>
    error instanceof HttpErrorResponse
      && (error.status >= 500
        || (error.status === 0
          && error.error instanceof ProgressEvent
          && error.error.type === 'error'));
}
